// Utility to retry dynamic imports (mitigates transient ChunkLoadError)
// Usage: const Page = lazyRetry(() => import('./pages/Page'));
import React from 'react';

// Store preload functions for each lazy component
const preloadRegistry = new Map<string, () => Promise<any>>();

export function lazyRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 3,
  interval = 1000
) {
  // Store factory for preloading
  const factoryKey = factory.toString();
  preloadRegistry.set(factoryKey, factory);

  const LazyComponent = React.lazy(() => {
    const load = (attempt: number): Promise<{ default: T }> =>
      factory().catch((err) => {
        const isChunkError = /ChunkLoadError|Loading chunk[\w\s:/().-]*failed/i.test(err?.message || '');
        if (attempt <= 0 || !isChunkError) throw err;
        return new Promise((resolve, reject) => {
          setTimeout(() => {
            load(attempt - 1).then(resolve).catch(reject);
          }, interval);
        });
      });
    return load(retries);
  });

  // Attach preload function to component
  (LazyComponent as any).preload = factory;

  return LazyComponent;
}

/**
 * Preload a lazy component before it's needed
 * Usage: preloadComponent(Dashboard) or preloadComponent(() => import('./pages/Dashboard'))
 */
export function preloadComponent(componentOrFactory: any): void {
  try {
    if (typeof componentOrFactory === 'function') {
      // Direct factory function
      componentOrFactory().catch(() => {});
    } else if (componentOrFactory?.preload) {
      // Lazy component with preload attached
      componentOrFactory.preload().catch(() => {});
    }
  } catch {
    // Silently fail - preloading is optional optimization
  }
}

/**
 * Prefetch critical routes after initial page load
 * Call this once in App component after mount
 */
export function prefetchCriticalRoutes(factories: Array<() => Promise<any>>): void {
  // Use requestIdleCallback if available, otherwise setTimeout
  const schedulePreload = (callback: () => void) => {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(callback, { timeout: 3000 });
    } else {
      setTimeout(callback, 1000);
    }
  };

  schedulePreload(() => {
    factories.forEach((factory, index) => {
      // Stagger preloads to avoid network congestion
      setTimeout(() => {
        factory().catch(() => {});
      }, index * 200);
    });
  });
}

export default lazyRetry;
